import { useEffect, useRef } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import FocusTrap from 'focus-trap-react';
import { X, ChevronRight } from 'lucide-react';

const FullscreenModal = ({ isOpen, onClose, children, title, hashId, breadcrumb, zIndex, scrollContainerRef }) => {
  const dragControls = useDragControls();
  const internalScrollRef = useRef(null);

  // Expose scroll container ref to parent if requested
  const scrollRefCallback = (node) => {
    internalScrollRef.current = node;
    if (scrollContainerRef) {
      if (typeof scrollContainerRef === 'function') {
        scrollContainerRef(node);
      } else {
        scrollContainerRef.current = node;
      }
    }
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && hashId) window.history.pushState(null, '', `/#${hashId}`);
    if (!isOpen && hashId && window.location.hash === `#${hashId}`) window.history.pushState(null, '', '/');
  }, [isOpen, hashId]);

  useEffect(() => {
    const handlePop = () => { if (isOpen) onClose(); };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [isOpen, onClose]);

  const handleDragEnd = (_, info) => {
    if (info.offset.y > 150 || info.velocity.y > 600) onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed inset-0 ${zIndex || 'z-[100]'}`}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onWheel={(e) => e.stopPropagation()}
        >
          <motion.div
            className="absolute inset-0 bg-black/60"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />

          <FocusTrap focusTrapOptions={{ allowOutsideClick: true, escapeDeactivates: false }}>
          <motion.div
            className="absolute inset-x-0 bottom-0 top-0 md:top-[3vh] md:inset-x-[2vw] lg:inset-x-[5vw] bg-white overflow-hidden flex flex-col"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 350, mass: 0.7 }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
          >
            <div
              className="flex justify-center pt-3 pb-1 md:hidden cursor-grab active:cursor-grabbing"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-10 h-1 bg-black/15 rounded-full" />
            </div>

            {/* Sticky header with breadcrumb */}
            <div className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-10 h-14 md:h-16 bg-white/90 backdrop-blur-xl border-b border-black/5">
              <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 min-w-0">
                <button
                  onClick={onClose}
                  className="font-oswald text-[10px] md:text-xs uppercase tracking-widest text-black/30 hover:text-[#FFC107] transition-colors flex-shrink-0"
                >
                  Home
                </button>
                {breadcrumb ? (
                  breadcrumb.map((item, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <ChevronRight size={10} className="text-black/15 flex-shrink-0" aria-hidden="true" />
                      {item.onClick ? (
                        <button
                          onClick={item.onClick}
                          className="font-oswald text-[10px] md:text-xs uppercase tracking-widest text-black/30 hover:text-[#FFC107] transition-colors truncate"
                        >
                          {item.label}
                        </button>
                      ) : (
                        <span className="font-oswald text-[10px] md:text-xs uppercase tracking-widest text-black/60 truncate">
                          {item.label}
                        </span>
                      )}
                    </span>
                  ))
                ) : (
                  <span className="flex items-center gap-1.5">
                    <ChevronRight size={10} className="text-black/15 flex-shrink-0" aria-hidden="true" />
                    <span className="font-oswald text-[10px] md:text-xs uppercase tracking-widest text-black/60 truncate">
                      {title}
                    </span>
                  </span>
                )}
              </nav>
              <motion.button
                onClick={onClose}
                aria-label="Fechar"
                className="w-10 h-10 flex items-center justify-center hover:bg-black/5 transition-colors rounded-full flex-shrink-0 ml-2"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X size={18} aria-hidden="true" />
              </motion.button>
            </div>

            <div
              ref={scrollRefCallback}
              className="flex-1 overflow-y-auto overscroll-contain"
              data-lenis-prevent
              style={{ willChange: 'scroll-position', WebkitOverflowScrolling: 'touch' }}
            >
              {children}
            </div>
          </motion.div>
          </FocusTrap>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FullscreenModal;
